import Cookies from 'js-cookie';
import React, { FC, useCallback, useEffect, useState } from 'react';

import { noNeedForPopup, requestPopup } from '@eika-infrastruktur/popup-lib';
import { LinkButton } from '@eika/button';
import { InfoCard } from '@eika/card';
import { Link, LinkType } from '@eika/link';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { EikaModal } from '../../components/EikaModal';
import { useConfig } from '../../defaultConfig';
import { usePushDevice } from '../../fetchHooks/usePushDevice';
import useTranslation from '../../hooks/useTranslation';
import { useUnactivatedPush } from '../../hooks/useUnactivatedPush';
import { pushTrackIDs } from '../../utils/gtm/trackIDs/push';

import './PushPopup.scss';

const THREE_DAYS = 3;
const SIXTY_DAYS = 60;

const PushPopup: FC = () => {
    const {
        pushSettingsLink,
        featureToggles: { disablePushPopup },
    } = useConfig();

    const [openModal, setOpenModal] = useState(false);

    const showPopupCookieName = 'show_push_popup';
    const showModal = Cookies.get(showPopupCookieName) === undefined;

    const [expiersInDays, setExpiresInDays] = useState(THREE_DAYS);

    const couldHavePush = useUnactivatedPush(showModal);

    const { data: pushDevice, isSuccess, isError, isLoading } = usePushDevice(couldHavePush);
    const translated = useTranslation();

    const [open, setOpen] = useState(true);

    const setCookie = useCallback(() => {
        Cookies.set(showPopupCookieName, 'closed', { expires: expiersInDays });
    }, [expiersInDays]);

    useEffect(() => {
        if (!isLoading && (isSuccess || isError)) {
            if (couldHavePush && isSuccess && pushDevice?.enabled === false && !disablePushPopup) {
                requestPopup('pushNotification', () => setOpenModal(true));
            } else {
                noNeedForPopup('pushNotification');
            }
        }
    }, [couldHavePush, disablePushPopup, isError, isLoading, isSuccess, pushDevice?.enabled]);

    if (openModal) {
        return (
            <EikaModal id="push-popup-modal" open={open} setOpen={setOpen} onClose={setCookie} contentShouldStretch>
                <InfoCard skipBackground>
                    <RichIcon type={RichIconTypes.MOBILBANK_APP_KUN_TELEFON} />
                    <InfoCard.TitleH3>{translated.pushPopup.title}</InfoCard.TitleH3>

                    <p className="typography-ingress">{translated.pushPopup.description}</p>
                    <Link
                        linkType={LinkType.BUTTON_STANDARD_POSITIVE}
                        href={pushSettingsLink!.url}
                        data-track-id={pushTrackIDs.popup}
                        data-track-linkname={translated.pushPopup.buttonText}
                        onClick={() => setOpen(false)}
                    >
                        {translated.pushPopup.buttonText}
                    </Link>
                    <LinkButton
                        size="large"
                        onClick={() => {
                            setExpiresInDays(SIXTY_DAYS);
                            setOpen(false);
                        }}
                        data-track-id={pushTrackIDs.popup}
                        data-track-linkname={translated.pushPopup.notNow}
                    >
                        {translated.pushPopup.notNow}
                    </LinkButton>
                </InfoCard>
            </EikaModal>
        );
    }

    return null;
};

export default PushPopup;
